<template>
  <!-- Backdrop para cerrar dropdown al tocar afuera -->
  <div v-if="activeDropdown" class="dropdown-backdrop" @click="activeDropdown = null" />

  <div class="tb-wrap">
    <div class="search-form">
      <div class="form-title">¿Cómo llegar?</div>

      <div class="route-block">
        <!-- ── Origen ── -->
        <div class="field-group">
          <div class="field-dot field-dot--a" />
          <div class="field-body">
            <div class="field-label">Origen</div>

            <!-- Selector (modo normal) -->
            <div v-if="origenSelection !== 'otra'" class="field-selector" @click="toggleDropdown('origen')">
              <template v-if="origenSelection">
                <div class="sel-icon" :class="selIconClass(origenSelection)">
                  <i :class="selectionIcon(origenSelection)" />
                </div>
                <span class="sel-name">{{ selectionName(origenSelection, 'origen') }}</span>
                <button class="sel-clear" @click.stop="clearField('origen')">
                  <i class="pi pi-times-circle" />
                </button>
              </template>
              <template v-else>
                <span class="sel-placeholder">Elegir lugar...</span>
                <i class="pi pi-chevron-down sel-chevron" :class="{ 'sel-chevron--up': activeDropdown === 'origen' }" />
              </template>
            </div>

            <!-- Buscador (modo "Otra dirección") -->
            <div v-else class="field-row">
              <input
                v-model="origenQuery"
                class="field-input"
                placeholder="Buscar dirección..."
                autofocus
                @keyup.enter="geocodificar(origenQuery, 'origen')"
              />
              <div class="field-actions">
                <i v-if="origenLatlng" class="pi pi-check-circle field-check" />
                <button class="field-btn" @click="geocodificar(origenQuery, 'origen')"><i class="pi pi-search" /></button>
                <button class="field-btn" :class="{ 'field-btn--on': mapMode === 'origen' }" @click="toggleMapMode('origen')"><i class="pi pi-map" /></button>
                <button class="field-btn" @click="clearField('origen')"><i class="pi pi-times" /></button>
              </div>
            </div>

            <!-- Dropdown origen -->
            <div v-if="activeDropdown === 'origen'" class="dropdown">
              <div class="dropdown-opt" @click="selectOption('origen', 'location')">
                <div class="opt-icon opt-icon--location"><i class="pi pi-map-marker" /></div>
                <span class="opt-name">Mi ubicación actual</span>
                <i v-if="origenSelection === 'location'" class="pi pi-check opt-check" />
              </div>

              <template v-if="allLugares.length">
                <div class="dropdown-sep" />
                <div
                  v-for="(lugar, idx) in allLugares"
                  :key="idx"
                  class="dropdown-opt"
                  @click="selectOption('origen', lugar)"
                >
                  <div class="opt-icon" :class="`opt-icon--${lugar._type || 'saved'}`">
                    <i :class="iconForType(lugar._type)" />
                  </div>
                  <span class="opt-name">{{ lugar.name }}</span>
                  <i v-if="origenSelection === lugar" class="pi pi-check opt-check" />
                </div>
              </template>

              <div class="dropdown-sep" />
              <div class="dropdown-opt" @click="selectOption('origen', 'otra')">
                <div class="opt-icon opt-icon--otra"><i class="pi pi-search" /></div>
                <span class="opt-name">Otra dirección...</span>
              </div>

              <div class="dropdown-footer">
                <button @click.stop="showMisLugares = true; activeDropdown = null">
                  <i class="pi pi-pencil" />
                  {{ allLugares.length ? 'Gestionar mis lugares' : 'Guardar mis lugares' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="connector-row"><div class="connector-line" /></div>

        <!-- ── Destino ── -->
        <div class="field-group">
          <div class="field-dot field-dot--b" />
          <div class="field-body">
            <div class="field-label">Destino</div>

            <div v-if="destinoSelection !== 'otra'" class="field-selector" @click="toggleDropdown('destino')">
              <template v-if="destinoSelection">
                <div class="sel-icon" :class="selIconClass(destinoSelection)">
                  <i :class="selectionIcon(destinoSelection)" />
                </div>
                <span class="sel-name">{{ selectionName(destinoSelection, 'destino') }}</span>
                <button class="sel-clear" @click.stop="clearField('destino')">
                  <i class="pi pi-times-circle" />
                </button>
              </template>
              <template v-else>
                <span class="sel-placeholder">Elegir lugar...</span>
                <i class="pi pi-chevron-down sel-chevron" :class="{ 'sel-chevron--up': activeDropdown === 'destino' }" />
              </template>
            </div>

            <div v-else class="field-row">
              <input
                v-model="destinoQuery"
                class="field-input"
                placeholder="Buscar dirección..."
                autofocus
                @keyup.enter="geocodificar(destinoQuery, 'destino')"
              />
              <div class="field-actions">
                <i v-if="destinoLatlng" class="pi pi-check-circle field-check" />
                <button class="field-btn" @click="geocodificar(destinoQuery, 'destino')"><i class="pi pi-search" /></button>
                <button class="field-btn" :class="{ 'field-btn--on': mapMode === 'destino' }" @click="toggleMapMode('destino')"><i class="pi pi-map" /></button>
                <button class="field-btn" @click="clearField('destino')"><i class="pi pi-times" /></button>
              </div>
            </div>

            <!-- Dropdown destino -->
            <div v-if="activeDropdown === 'destino'" class="dropdown">
              <div class="dropdown-opt" @click="selectOption('destino', 'location')">
                <div class="opt-icon opt-icon--location"><i class="pi pi-map-marker" /></div>
                <span class="opt-name">Mi ubicación actual</span>
                <i v-if="destinoSelection === 'location'" class="pi pi-check opt-check" />
              </div>

              <template v-if="allLugares.length">
                <div class="dropdown-sep" />
                <div
                  v-for="(lugar, idx) in allLugares"
                  :key="idx"
                  class="dropdown-opt"
                  @click="selectOption('destino', lugar)"
                >
                  <div class="opt-icon" :class="`opt-icon--${lugar._type || 'saved'}`">
                    <i :class="iconForType(lugar._type)" />
                  </div>
                  <span class="opt-name">{{ lugar.name }}</span>
                  <i v-if="destinoSelection === lugar" class="pi pi-check opt-check" />
                </div>
              </template>

              <div class="dropdown-sep" />
              <div class="dropdown-opt" @click="selectOption('destino', 'otra')">
                <div class="opt-icon opt-icon--otra"><i class="pi pi-search" /></div>
                <span class="opt-name">Otra dirección...</span>
              </div>

              <div class="dropdown-footer">
                <button @click.stop="showMisLugares = true; activeDropdown = null">
                  <i class="pi pi-pencil" />
                  {{ allLugares.length ? 'Gestionar mis lugares' : 'Guardar mis lugares' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="error" class="form-error">{{ error }}</div>

      <div v-if="mapMode" class="map-hint">
        <i class="pi pi-map-marker" />
        Tocá en el mapa para marcar el {{ mapMode === 'origen' ? 'origen' : 'destino' }}
      </div>

      <div class="form-actions">
        <button
          class="btn-search"
          :disabled="!origenLatlng || !destinoLatlng || buscando"
          @click="buscar()"
        >
          <i class="pi pi-directions btn-search-icon" />
          {{ buscando ? 'Buscando...' : 'Buscar rutas' }}
        </button>
        <button class="btn-clear" title="Limpiar todo" @click="limpiar">
          <i class="pi pi-times" />
        </button>
      </div>
    </div>

    <!-- Resultados -->
    <div class="results">
      <template v-if="resultados">
        <div v-if="resultados.sinResultados" class="empty-state">
          <div class="empty-icon"><i class="pi pi-map" /></div>
          <div class="empty-title">Sin líneas cercanas</div>
          <div class="empty-body">Probá ampliar el radio o agregar más paradas a las líneas.</div>
        </div>

        <div v-if="resultados.directas.length" class="results-group">
          <div class="group-label">Sin cambios</div>
          <div
            v-for="(r, i) in resultados.directas"
            :key="i"
            class="result-card"
            :style="`--accent:${r.linea.color}`"
          >
            <div class="result-header">
              <span class="line-badge" :style="`background:${r.linea.color}`">{{ r.linea.numero }}</span>
              <span class="result-dir">{{ r.sentido }}</span>
              <span v-if="r.linea.horario?.[r.sentido]?.inicio" class="result-time">
                {{ r.linea.horario[r.sentido].inicio }}–{{ r.linea.horario[r.sentido].fin }}
              </span>
            </div>
            <div class="result-steps">
              <div class="step">
                <span class="step-verb">Subí</span> en {{ r.paradaOrigen.nombre || 'parada' }}
                <span class="step-dist">(~{{ r.distOrigen }}m a pie)</span>
              </div>
              <div class="step">
                <span class="step-verb">Bajá</span> en {{ r.paradaDestino.nombre || 'parada' }}
                <span class="step-dist">(~{{ r.distDestino }}m al destino)</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="resultados.conTransbordo.length" class="results-group">
          <div class="group-label">Con cambio</div>
          <div
            v-for="(r, i) in resultados.conTransbordo"
            :key="i"
            class="result-card"
          >
            <div class="result-header">
              <span class="line-badge" :style="`background:${r.lineaIda.color}`">{{ r.lineaIda.numero }}</span>
              <i class="pi pi-arrow-right transfer-arrow" />
              <span class="line-badge" :style="`background:${r.lineaVuelta.color}`">{{ r.lineaVuelta.numero }}</span>
            </div>
            <div class="result-steps">
              <div class="step">
                Subí en {{ r.paradaIda.nombre || 'parada' }}
                <span class="step-dist">(~{{ r.distOrigen }}m)</span>
                → bajá en {{ r.paradaTransbordoBajada.nombre || 'parada trasbordo' }}
              </div>
              <div class="step">
                Tomá la {{ r.lineaVuelta.numero }} desde {{ r.paradaTransbordoSubida.nombre || 'parada trasbordo' }}
                → bajá en {{ r.paradaDestino.nombre || 'parada' }}
                <span class="step-dist">(~{{ r.distDestino }}m)</span>
              </div>
            </div>
          </div>
        </div>

        <TransporteUber :origen="origenLatlng" :destino="destinoLatlng" />
      </template>

      <div v-else class="results-placeholder">
        <i class="pi pi-directions placeholder-icon" />
        <span>Elegí origen y destino para buscar cómo llegar</span>
      </div>
    </div>
  </div>

  <MisLugares v-model:visible="showMisLugares" :userLatLng="userLatLng" />
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useTransporteBuscador } from '@/composables/useTransporteBuscador'
import { useSavedPlaces } from '@/composables/useSavedPlaces'
import TransporteUber from './TransporteUber.vue'
import MisLugares from '@/components/SalidaPublica/MisLugares.vue'

const props = defineProps({
  lineas: Array,
  sugerencias: { type: Array, default: () => [] },
  userLatLng: { type: Array, default: null },
})

const emit = defineEmits(['setMapMode'])

const { places: savedPlaces } = useSavedPlaces()
const showMisLugares = ref(false)

const allLugares = computed(() => [
  ...props.sugerencias.map(s => ({ ...s, _type: s._type || 'pe' })),
  ...savedPlaces.value.map(p => ({ ...p, _type: 'saved' })),
])

// ── Estado del dropdown ────────────────────────────────────────────────────────
const activeDropdown = ref(null) // 'origen' | 'destino' | null
const origenSelection = ref(null) // null | 'location' | 'otra' | lugar
const destinoSelection = ref(null)

const mapMode = ref(null)

const {
  origenLatlng,
  destinoLatlng,
  origenQuery,
  destinoQuery,
  resultados,
  buscando,
  error,
  geocodificar,
  buscar,
  limpiar: limpiarBuscador,
} = useTransporteBuscador(() => props.lineas)

// ── Helpers de visualización ───────────────────────────────────────────────────
const iconForType = (type) => {
  if (type === 'pe') return 'pi pi-map-marker'
  if (type === 'pet') return 'pi pi-flag'
  return 'pi pi-home'
}

const selectionIcon = (sel) => {
  if (sel === 'location') return 'pi pi-map-marker'
  if (sel?._type === 'pe') return 'pi pi-map-marker'
  if (sel?._type === 'pet') return 'pi pi-flag'
  return 'pi pi-home'
}

const selIconClass = (sel) => {
  if (sel === 'location') return 'sel-icon--location'
  if (sel?._type === 'pe') return 'sel-icon--pe'
  if (sel?._type === 'pet') return 'sel-icon--pet'
  return 'sel-icon--saved'
}

const locatingOrigen = ref(false)
const locatingDestino = ref(false)

const selectionName = (sel, field) => {
  if (sel === 'location') {
    const latlng = field === 'origen' ? origenLatlng.value : destinoLatlng.value
    if (latlng) return 'Mi ubicación actual'
    const locating = field === 'origen' ? locatingOrigen.value : locatingDestino.value
    return locating ? 'Buscando ubicación...' : 'No se pudo obtener la ubicación'
  }
  return sel?.name || ''
}

// ── Acciones de dropdown ───────────────────────────────────────────────────────
const toggleDropdown = (field) => {
  activeDropdown.value = activeDropdown.value === field ? null : field
}

// ── Pedir GPS directamente cuando el padre todavía no tiene ubicación ────────
const requestGeolocation = (field, setField) => {
  const locating = field === 'origen' ? locatingOrigen : locatingDestino
  if (!navigator.geolocation) {
    error.value = 'Este navegador no admite geolocalización.'
    locating.value = false
    return
  }
  locating.value = true
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      locating.value = false
      // El usuario pudo haber cambiado de selección mientras esperábamos el GPS
      const stillSelected = (field === 'origen' ? origenSelection.value : destinoSelection.value) === 'location'
      if (!stillSelected) return
      setField.latlng({ lat: pos.coords.latitude, lng: pos.coords.longitude })
      setField.query('Mi ubicación actual')
    },
    () => {
      locating.value = false
      error.value = 'No se pudo obtener tu ubicación. Revisá los permisos de geolocalización del navegador.'
    },
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
  )
}

const selectOption = (field, option) => {
  activeDropdown.value = null

  const setField = (field === 'origen')
    ? {
        sel: v => { origenSelection.value = v },
        latlng: v => { origenLatlng.value = v },
        query: v => { origenQuery.value = v },
      }
    : {
        sel: v => { destinoSelection.value = v },
        latlng: v => { destinoLatlng.value = v },
        query: v => { destinoQuery.value = v },
      }

  setField.sel(option)

  if (option === 'location') {
    const ll = props.userLatLng
    if (ll) {
      setField.latlng({ lat: ll[0], lng: ll[1] })
      setField.query('Mi ubicación actual')
    } else {
      setField.latlng(null)
      setField.query('')
      requestGeolocation(field, setField)
    }
  } else if (option === 'otra') {
    setField.latlng(null)
    setField.query('')
  } else {
    setField.latlng({ lat: option.lat, lng: option.lng })
    setField.query(option.name)
  }
}

const clearField = (field) => {
  if (field === 'origen') {
    origenSelection.value = null
    origenLatlng.value = null
    origenQuery.value = ''
  } else {
    destinoSelection.value = null
    destinoLatlng.value = null
    destinoQuery.value = ''
  }
}

// ── Mapa ───────────────────────────────────────────────────────────────────────
const toggleMapMode = (target) => {
  mapMode.value = mapMode.value === target ? null : target
  emit('setMapMode', mapMode.value)
}

// ── Limpiar todo ───────────────────────────────────────────────────────────────
const limpiar = () => {
  limpiarBuscador()
  origenSelection.value = null
  destinoSelection.value = null
  mapMode.value = null
  activeDropdown.value = null
  emit('setMapMode', null)
}

// ── Actualizar "Mi ubicación" cuando llega el GPS ─────────────────────────────
watch(() => props.userLatLng, (ll) => {
  if (!ll) return
  const coords = { lat: ll[0], lng: ll[1] }
  if (origenSelection.value === 'location') origenLatlng.value = coords
  if (destinoSelection.value === 'location') destinoLatlng.value = coords
})

// ── API pública (usada por el padre vía ref) ───────────────────────────────────
const setLatlng = (target, rawLatlng, label) => {
  if (!rawLatlng) return
  const coords = Array.isArray(rawLatlng)
    ? { lat: rawLatlng[0], lng: rawLatlng[1] }
    : rawLatlng

  // Intentar hacer match con un lugar conocido
  const matching = allLugares.value.find(l =>
    l.lat != null &&
    Math.abs(l.lat - coords.lat) < 0.001 &&
    Math.abs(l.lng - coords.lng) < 0.001
  )
  if (matching) {
    selectOption(target, matching)
    return
  }

  const display = label || (coords.lat != null ? `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}` : '')
  if (target === 'origen') {
    origenLatlng.value = coords
    origenQuery.value = display
    origenSelection.value = 'otra'
  } else {
    destinoLatlng.value = coords
    destinoQuery.value = display
    destinoSelection.value = 'otra'
  }
  mapMode.value = null
  emit('setMapMode', null)
}

defineExpose({ origenLatlng, destinoLatlng, setLatlng, mapMode: computed(() => mapMode.value) })
</script>

<style scoped>
/* ─── Layout ─────────────────────────────────────────────────────────────────── */
.tb-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif;
  background: var(--bg-grouped);
}

/* ─── Backdrop ───────────────────────────────────────────────────────────────── */
.dropdown-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99;
}

/* ─── Search form ────────────────────────────────────────────────────────────── */
.search-form {
  flex-shrink: 0;
  background: var(--bg-primary);
  border-bottom: 1px solid rgba(60, 60, 67, 0.12);
  padding: 14px 16px 12px;
}

.form-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--label-primary);
  margin-bottom: 12px;
}

/* ─── Route block ────────────────────────────────────────────────────────────── */
.route-block {
  margin-bottom: 12px;
}

.field-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.field-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.field-dot--a {
  background: var(--success);
  box-shadow: 0 0 0 3px rgba(52, 199, 89, 0.18);
}

.field-dot--b {
  background: var(--danger);
  box-shadow: 0 0 0 3px rgba(255, 59, 48, 0.18);
}

.connector-row {
  height: 10px;
  padding-left: 4px;
}

.connector-line {
  width: 2px;
  height: 100%;
  background: rgba(60, 60, 67, 0.12);
  border-radius: 1px;
}

.field-body {
  flex: 1;
  min-width: 0;
  position: relative;
}

.field-label {
  font-size: 11px;
  font-weight: 500;
  color: rgba(60, 60, 67, 0.42);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 5px;
}

/* ─── Selector (modo normal) ─────────────────────────────────────────────────── */
.field-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  background: var(--bg-grouped);
  border-radius: 10px;
  padding: 0 10px 0 10px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.12s;
}

.field-selector:active { background: var(--bg-secondary); }

.sel-icon {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  flex-shrink: 0;
}

.sel-icon--location,
.sel-icon--pe  { background: var(--accent-soft); color: var(--accent); }
.sel-icon--pet { background: var(--success-soft); color: var(--success-deep); }
.sel-icon--saved { background: rgba(60,60,67,0.08); color: rgba(60,60,67,0.45); }

.sel-name {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: var(--label-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sel-placeholder {
  flex: 1;
  font-size: 14px;
  color: rgba(60, 60, 67, 0.32);
}

.sel-chevron {
  font-size: 10px;
  color: rgba(60, 60, 67, 0.38);
  flex-shrink: 0;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.sel-chevron--up { transform: rotate(180deg); }

.sel-clear {
  background: none;
  border: none;
  padding: 0;
  color: rgba(60, 60, 67, 0.32);
  cursor: pointer;
  font-size: 15px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  -webkit-tap-highlight-color: transparent;
  min-width: 24px;
  min-height: 24px;
  justify-content: center;
}

.sel-clear:active { color: rgba(60, 60, 67, 0.6); }

/* ─── Dropdown ───────────────────────────────────────────────────────────────── */
.dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--bg-primary);
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.08), 0 0 0 0.5px rgba(0, 0, 0, 0.06);
  z-index: 100;
  overflow: hidden;
}

.dropdown-opt {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.1s;
}

.dropdown-opt:active { background: rgba(0, 0, 0, 0.04); }

.opt-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.opt-icon--location,
.opt-icon--pe   { background: var(--accent-soft); color: var(--accent); }
.opt-icon--pet  { background: var(--success-soft); color: var(--success-deep); }
.opt-icon--saved,
.opt-icon--otra { background: rgba(60, 60, 67, 0.07); color: rgba(60, 60, 67, 0.45); }

.opt-name {
  flex: 1;
  font-size: 14px;
  color: var(--label-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.opt-check {
  font-size: 12px;
  color: var(--accent);
  flex-shrink: 0;
}

.dropdown-sep {
  height: 1px;
  background: rgba(60, 60, 67, 0.08);
  margin: 0 14px;
}

.dropdown-footer {
  border-top: 1px solid rgba(60, 60, 67, 0.08);
  padding: 8px 14px 10px;
}

.dropdown-footer button {
  background: none;
  border: none;
  font-size: 12px;
  color: rgba(60, 60, 67, 0.42);
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
}

.dropdown-footer button:active { color: rgba(60, 60, 67, 0.7); }

/* ─── Buscador inline ("Otra dirección") ─────────────────────────────────────── */
.field-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.field-input {
  flex: 1;
  min-width: 0;
  height: 38px;
  background: var(--bg-grouped);
  border: none;
  border-radius: 10px;
  padding: 0 10px;
  font-size: 14px;
  color: var(--label-primary);
  outline: none;
  -webkit-appearance: none;
  font-family: inherit;
  transition: background 0.15s;
}

.field-input::placeholder { color: rgba(60, 60, 67, 0.32); }
.field-input:focus { background: var(--bg-secondary); }

.field-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.field-check {
  color: var(--success);
  font-size: 13px;
}

.field-btn {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: var(--bg-grouped);
  border: none;
  color: rgba(60, 60, 67, 0.52);
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.12s, color 0.12s;
}

.field-btn:active { background: rgba(0, 0, 0, 0.08); }
.field-btn--on { background: var(--accent); color: #fff; }

/* ─── Estados extra del formulario ───────────────────────────────────────────── */
.form-error {
  font-size: 12px;
  color: var(--danger);
  margin-bottom: 8px;
  padding: 0 2px;
}

.map-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(60, 60, 67, 0.52);
  background: var(--bg-grouped);
  border-radius: 9px;
  padding: 8px 10px;
  margin-bottom: 10px;
}

/* ─── Botones de acción ───────────────────────────────────────────────────────── */
.form-actions {
  display: flex;
  gap: 8px;
}

.btn-search {
  flex: 1;
  height: 44px;
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  -webkit-tap-highlight-color: transparent;
  transition: opacity 0.12s;
}

.btn-search:disabled { opacity: 0.35; cursor: default; }
.btn-search:not(:disabled):active { opacity: 0.78; }
.btn-search-icon { font-size: 16px; }

.btn-clear {
  width: 44px;
  height: 44px;
  background: var(--bg-grouped);
  border: none;
  border-radius: 12px;
  color: rgba(60, 60, 67, 0.52);
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.12s;
}

.btn-clear:active { background: rgba(0, 0, 0, 0.08); }

/* ─── Resultados ─────────────────────────────────────────────────────────────── */
.results {
  flex: 1;
  overflow: auto;
  padding: 12px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 32px 16px;
  text-align: center;
}

.empty-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: rgba(60, 60, 67, 0.07);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: rgba(60, 60, 67, 0.35);
  margin-bottom: 6px;
}

.empty-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--label-primary);
}

.empty-body {
  font-size: 13px;
  color: rgba(60, 60, 67, 0.48);
  max-width: 220px;
  line-height: 1.4;
}

.results-group { margin-bottom: 20px; }

.group-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: rgba(60, 60, 67, 0.4);
  margin-bottom: 8px;
  padding: 0 4px;
}

.result-card {
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.04);
  border-left: 3px solid var(--accent, #007aff);
}

.result-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.line-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  min-width: 34px;
}

.result-dir {
  font-size: 12px;
  color: rgba(60, 60, 67, 0.5);
}

.result-time {
  font-size: 12px;
  color: rgba(60, 60, 67, 0.4);
  margin-left: auto;
}

.transfer-arrow {
  font-size: 9px;
  color: rgba(60, 60, 67, 0.3);
}

.result-steps {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.step {
  font-size: 13px;
  color: var(--label-secondary);
  line-height: 1.45;
}

.step-verb {
  font-weight: 600;
  color: var(--label-primary);
}

.step-dist {
  color: rgba(60, 60, 67, 0.42);
  font-size: 12px;
}

.results-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 16px;
  text-align: center;
}

.placeholder-icon {
  font-size: 30px;
  color: rgba(60, 60, 67, 0.16);
  margin-bottom: 2px;
}

.results-placeholder span {
  font-size: 13px;
  color: rgba(60, 60, 67, 0.4);
  max-width: 210px;
  line-height: 1.4;
}
</style>
